import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Card from '../components/Card';
import NavBar from '../components/NavBar';
import EducationalBanner from '../components/EducationalBanner';
import { useFirstVisit } from '../hooks/useFirstVisit';

function PeerDashboard() {
  const navigate = useNavigate();
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [sessionRequest, setSessionRequest] = useState({ topic: '', message: '' });
  const { isFirstVisit, setVisited } = useFirstVisit('peer');
  const [showEducationalBanner, setShowEducationalBanner] = useState(false);

  const mentors = [
    {
      id: 1,
      name: "Alex Chen",
      badges: ["Active Listener", "Stress Management", "Academic Support"],
      avatar: "👨‍💼",
      rating: 4.9,
      sessions: 47,
      specialty: "Academic pressure and time management"
    },
    {
      id: 2,
      name: "Maya Patel", 
      badges: ["Peer Counselor", "Anxiety Support", "Mindfulness Guide"],
      avatar: "👩‍🎓",
      rating: 4.8,
      sessions: 32,
      specialty: "Social anxiety and self-confidence"
    },
    {
      id: 3,
      name: "Jordan Kim",
      badges: ["Crisis Support", "Mental Health First Aid"],
      avatar: "🧑‍💻",
      rating: 5.0,
      sessions: 23,
      specialty: "Identity issues and family support"
    }
  ];

  // Scroll to top on mount and check for first visit
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (isFirstVisit) {
      setShowEducationalBanner(true);
    }
  }, [isFirstVisit]);

  function requestSession(mentorId) {
    setSelectedMentor(mentors.find(m => m.id === mentorId));
  }

  function submitRequest() {
    alert(`Session request sent to ${selectedMentor.name}! They will respond within 24 hours.`);
    setSelectedMentor(null);
    setSessionRequest({ topic: '', message: '' });
  }

  // Handle closing the educational banner
  const handleCloseBanner = () => {
    setShowEducationalBanner(false);
    setVisited();
  };

  return (
    <div style={{ backgroundColor: '#f5f7fa', minHeight: '100vh', paddingTop: '60px' }}>
      <NavBar title="🤝 Peer Support Network" />
      
      {/* Educational Banner - Only shows on first visit */}
      {showEducationalBanner && (
        <EducationalBanner 
          feature="peer" 
          onDismiss={handleCloseBanner} 
        />
      )}
      
      <div style={{ maxWidth: '480px', margin: 'auto', padding: '20px' }}>
        <Card style={{ textAlign: 'center' }}>
          <h1 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '700' }}>🤝 Peer Support Network</h1>
          <p style={{ color: '#64748b', margin: '0 0 16px 0' }}>
            Connect with trained peer mentors for confidential support
          </p>
        </Card>

        <Card style={{ backgroundColor: '#e3f2fd' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ color: '#1565c0', margin: '0 0 4px 0', fontWeight: '600' }}>Your Support Network</h3>
              <p style={{ color: '#1976d2', margin: 0, fontSize: '14px' }}>Anonymous • Confidential • Always Available</p>
            </div>
            <div style={{ fontSize: '32px' }}>🛡️</div>
          </div>
        </Card>

        <h2 style={{ color: '#2c3e50', margin: '20px 0 12px 0', fontWeight: '600' }}>Available Mentors</h2>

        {mentors.map(mentor => (
          <Card key={mentor.id}>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ 
                fontSize: '36px', 
                marginRight: '12px',
                width: '50px',
                height: '50px',
                backgroundColor: '#f0f0f0',
                borderRadius: '25px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {mentor.avatar}
              </div>
              
              <div style={{ flex: 1 }}>
                <h3 style={{ color: '#2c3e50', margin: '0 0 4px 0', fontWeight: '600' }}>{mentor.name}</h3>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ color: '#ff9800', marginRight: '4px' }}>⭐</span>
                  <span style={{ fontSize: '14px', color: '#7f8c8d' }}>{mentor.rating} • {mentor.sessions} sessions</span>
                </div>
                <p style={{ color: '#7f8c8d', fontSize: '12px', margin: '4px 0' }}>{mentor.specialty}</p>
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '12px' }}>
              {mentor.badges.map((badge, index) => (
                <span key={index} style={{
                  backgroundColor: '#e8f5e8',
                  color: '#2e7d32',
                  fontSize: '11px',
                  padding: '2px 6px',
                  borderRadius: '12px',
                  border: '1px solid #c8e6c9'
                }}>
                  {badge}
                </span>
              ))}
            </div>

            <Button 
              onClick={() => requestSession(mentor.id)}
              style={{ width: '100%', margin: 0, backgroundColor: '#2196f3' }}
            >
              Request Session
            </Button>
          </Card>
        ))}

        <Card>
          <h3 style={{ color: '#2c3e50', marginBottom: '12px', fontWeight: '600' }}>📊 Your Mentorship Progress</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4caf50' }}>3</div>
              <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Sessions This Month</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196f3' }}>4.8</div>
              <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Average Rating</div>
            </div>
          </div>

          <div style={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#2c3e50', fontWeight: '600' }}>Become a Peer Mentor</h4>
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#7f8c8d' }}>
              Help others while developing leadership skills
            </p>
            <Button style={{ margin: 0, fontSize: '12px', padding: '6px 12px', backgroundColor: '#ff9800' }}>
              Start Training
            </Button>
          </div>
        </Card>

        {selectedMentor && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            zIndex: 1000
          }}>
            <Card style={{ width: '100%', maxWidth: '400px', margin: 0 }}>
              <h3 style={{ color: '#2c3e50', marginBottom: '16px', fontWeight: '600' }}>
                Request Session with {selectedMentor.name}
              </h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  What would you like to discuss?
                </label>
                <select
                  value={sessionRequest.topic}
                  onChange={(e) => setSessionRequest({...sessionRequest, topic: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '1px solid #ddd',
                    borderRadius: '8px'
                  }}
                >
                  <option value="">Select a topic...</option>
                  <option value="stress">Stress & Anxiety</option>
                  <option value="academic">Academic Pressure</option>
                  <option value="relationships">Relationships & Social Issues</option>
                  <option value="family">Family Problems</option>
                  <option value="identity">Identity & Self-Esteem</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Brief description (optional):
                </label>
                <textarea
                  value={sessionRequest.message}
                  onChange={(e) => setSessionRequest({...sessionRequest, message: e.target.value})}
                  placeholder="Share what's on your mind..."
                  style={{
                    width: '100%',
                    height: '80px',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    resize: 'none'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <Button 
                  onClick={() => setSelectedMentor(null)}
                  style={{ flex: 1, backgroundColor: '#9e9e9e', margin: 0 }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={submitRequest}
                  disabled={!sessionRequest.topic}
                  style={{ flex: 1, margin: 0 }}
                >
                  Send Request
                </Button>
              </div>
            </Card>
          </div>
        )}

        <div style={{ marginTop: '24px', display: 'flex', gap: '8px' }}>
          <Button onClick={() => navigate('/focus')} style={{ flex: 1, backgroundColor: '#ff5722' }}>
            ← Focus Bubble
          </Button>
          <Button onClick={() => navigate('/home')} style={{ flex: 1, backgroundColor: '#4361ee' }}>
            Dashboard →
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PeerDashboard;
